import React, { useEffect, useState } from "react";
import { Wallet, Clock, ArrowDownToLine, ExternalLink } from "lucide-react";
import { api, formatPrice } from "../lib/api";

const STATUS_STYLES = {
  paid: "bg-emerald-700 text-white",
  in_transit: "bg-emerald-900 text-white",
  pending: "bg-[var(--mn-ink)] text-[var(--mn-bg)]",
  canceled: "bg-neutral-300 text-[var(--mn-ink-2)]",
  failed: "bg-[var(--mn-accent)] text-white",
};

export default function StripePayoutsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/payouts");
        if (!cancelled) setData(r.data);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="h-64 bg-neutral-200/60 animate-pulse" data-testid="payouts-loading" />;
  }
  if (!data || !data.configured) return null;

  const available = data.available_usd ?? 0;
  const pending = data.pending_usd ?? 0;
  const sched = data.schedule || {};
  const payouts = data.payouts || [];

  return (
    <div className="border border-[var(--mn-border)] p-6 md:p-8" data-testid="stripe-payouts-widget">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="eyebrow">Treasury · Stripe</p>
          <h2 className="font-display text-2xl md:text-3xl mt-1 tracking-tight">Payouts</h2>
        </div>
        <a
          href="https://dashboard.stripe.com/payouts"
          target="_blank"
          rel="noreferrer"
          className="text-xs uppercase tracking-[0.22em] text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)] flex items-center gap-1.5"
          data-testid="payouts-stripe-link"
        >
          Stripe Dashboard <ExternalLink size={12} strokeWidth={1.5} />
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
        <Stat
          icon={<Wallet size={16} strokeWidth={1.4} />}
          label="Available now"
          value={formatPrice(available)}
          sub="Ready for next payout"
          testid="payout-available"
          warn={available < 0}
        />
        <Stat
          icon={<Clock size={16} strokeWidth={1.4} />}
          label="In transit"
          value={formatPrice(pending)}
          sub="Settling from charges"
          testid="payout-pending"
        />
        <Stat
          icon={<ArrowDownToLine size={16} strokeWidth={1.4} />}
          label="Schedule"
          value={describeSchedule(sched)}
          sub={sched.delay_days != null ? `${sched.delay_days}-day rolling delay` : "—"}
          testid="payout-schedule"
        />
      </div>

      <div>
        <p className="eyebrow mb-3">Recent payouts</p>
        {payouts.length === 0 ? (
          <p className="text-sm text-[var(--mn-ink-2)] py-6 border-t border-[var(--mn-border)]" data-testid="payouts-empty">
            No payouts yet. Once your first sales settle, payouts appear here.
          </p>
        ) : (
          <ul className="border-y border-[var(--mn-border)] divide-y divide-[var(--mn-border)]" data-testid="payouts-list">
            {payouts.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between gap-4" data-testid={`payout-${p.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono-luxe text-base">{formatPrice(p.amount)}</span>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] ${STATUS_STYLES[p.status] || "bg-neutral-200"}`}>
                      {(p.status || "").replace("_", " ")}
                    </span>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">
                    {p.method || "bank"} · {p.type || ""}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-[var(--mn-ink)]">
                    {p.status === "paid" ? "Arrived" : "Arrives"} <strong>{fmtDate(p.arrival_date)}</strong>
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">
                    Created {fmtDate(p.created)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, testid, warn }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 text-[var(--mn-ink-2)]">
        {icon}
        <p className="eyebrow">{label}</p>
      </div>
      <p className={`font-display text-2xl md:text-3xl tracking-tighter mt-1.5 ${warn ? "text-[var(--mn-accent)]" : ""}`}>
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">{sub}</p>
    </div>
  );
}

function describeSchedule(s) {
  if (!s || !s.interval) return "—";
  if (s.interval === "manual") return "Manual";
  if (s.interval === "daily") return "Daily";
  if (s.interval === "weekly") {
    const a = (s.weekly_anchor || "").charAt(0).toUpperCase() + (s.weekly_anchor || "").slice(1);
    return a ? `Weekly · ${a}` : "Weekly";
  }
  if (s.interval === "monthly") {
    return s.monthly_anchor ? `Monthly · day ${s.monthly_anchor}` : "Monthly";
  }
  return s.interval;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
